import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import AuthLayout from '../components/layout/AuthLayout';
import Button from '../components/ui/Button';
import { FormField, Input } from '../components/ui/Form';

const Login = () => {
    const navigate = useNavigate();
    const { login, verifyOTP } = useAuth();
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [otp, setOtp] = useState('');
    const [requiresOTP, setRequiresOTP] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const result = await login(formData);

        if (result.success) {
            if (result.requiresOTP) {
                setRequiresOTP(true);
                setUserEmail(result.email);
                toast.success('Verification code sent');
            } else {
                navigate('/dashboard');
            }
        } else {
            toast.error(result.message);
        }

        setLoading(false);
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setLoading(true);

        const result = await verifyOTP(userEmail, otp);
        if (result.success) navigate('/dashboard');
        else toast.error(result.message);

        setLoading(false);
    };

    return (
        <AuthLayout
            footnote={
                requiresOTP
                    ? "Didn't get the code? Check your spam folder, or contact the ICT service desk."
                    : 'Trouble signing in? Contact the ICT service desk.'
            }
        >
            {!requiresOTP ? (
                <>
                    <h1 className="text-xl font-semibold text-ink-900">Sign in</h1>
                    <p className="text-base text-ink-500 mt-1 mb-5">
                        Use your MyBank ICT account to continue.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <FormField label="Email address" htmlFor="login-email">
                            <div className="relative">
                                <Mail
                                    size={15}
                                    aria-hidden="true"
                                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none"
                                />
                                <Input
                                    id="login-email"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    autoComplete="email"
                                    className="pl-8"
                                    placeholder="name@mybank.com"
                                />
                            </div>
                        </FormField>

                        <FormField label="Password" htmlFor="login-password">
                            <div className="relative">
                                <Lock
                                    size={15}
                                    aria-hidden="true"
                                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none"
                                />
                                <Input
                                    id="login-password"
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                    autoComplete="current-password"
                                    className="pl-8 pr-9"
                                    placeholder="Enter your password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded text-ink-400 hover:text-ink-700 transition-colors duration-150"
                                >
                                    {showPassword ? (
                                        <EyeOff size={15} aria-hidden="true" />
                                    ) : (
                                        <Eye size={15} aria-hidden="true" />
                                    )}
                                </button>
                            </div>
                        </FormField>

                        <div className="flex items-center justify-between pt-0.5">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    name="remember"
                                    className="w-3.5 h-3.5 accent-[var(--primary)]"
                                />
                                <span className="text-base text-ink-700">Remember me</span>
                            </label>
                            <Link
                                to="/forgot-password"
                                className="text-base font-medium text-secondary-600 hover:text-secondary-700 transition-colors duration-150"
                            >
                                Forgot password?
                            </Link>
                        </div>

                        <Button type="submit" variant="primary" size="lg" block loading={loading}>
                            Sign in
                        </Button>
                    </form>
                </>
            ) : (
                <>
                    <h1 className="text-xl font-semibold text-ink-900">Verify it's you</h1>
                    <p className="text-base text-ink-500 mt-1 mb-5">
                        We sent a six-digit code to{' '}
                        <span className="text-ink-800 font-medium">{userEmail}</span>.
                    </p>

                    <form onSubmit={handleVerifyOtp} className="space-y-4">
                        <FormField label="Verification code" htmlFor="login-otp">
                            <input
                                id="login-otp"
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                                required
                                maxLength={6}
                                autoFocus
                                autoComplete="one-time-code"
                                placeholder="000000"
                                className="input text-center text-2xl font-semibold tracking-[0.35em] tabular"
                                style={{ height: 52 }}
                            />
                        </FormField>

                        <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            block
                            loading={loading}
                            disabled={otp.length < 6}
                        >
                            Verify code
                        </Button>

                        <Button variant="ghost" block onClick={() => setRequiresOTP(false)}>
                            Back to sign in
                        </Button>
                    </form>
                </>
            )}
        </AuthLayout>
    );
};

export default Login;
